
import { useEffect, useRef } from 'react'

/**
 * The three things a modal owes a keyboard user, in one place.
 *
 * Focus moves into the dialog when it opens, Tab cycles inside it rather than
 * wandering onto the page behind, and focus returns to whatever opened it when
 * it closes. The pack list had none of these, and the preset picker had the
 * first only, so Tab walked straight out of both onto a canvas the user could
 * not see.
 *
 * Attach the returned ref to the dialog element.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function useModal<T extends HTMLElement>({
  open = true,
  onClose,
}: {
  open?: boolean
  onClose?: () => void
} = {}) {
  const ref = useRef<T>(null)
  const opener = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    const dialog = ref.current
    if (!dialog) return

    // Remember who opened it, so focus has somewhere to go back to.
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null

    // Move focus in. The first control, or the dialog itself when it has none
    // yet — a panel that is still loading, say.
    const first = dialog.querySelector<HTMLElement>(FOCUSABLE)
    if (first) first.focus()
    else {
      dialog.tabIndex = -1
      dialog.focus()
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose?.()
        return
      }
      if (event.key !== 'Tab') return
      const dialogNow = ref.current
      if (!dialogNow) return
      const items = [...dialogNow.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (items.length === 0) return
      const firstItem = items[0]
      const lastItem = items[items.length - 1]
      const active = document.activeElement
      // Wrap at both ends, and pull focus back in if it has escaped.
      if (event.shiftKey && (active === firstItem || !dialogNow.contains(active))) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && (active === lastItem || !dialogNow.contains(active))) {
        event.preventDefault()
        firstItem.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      // Only take focus back if it is still inside the dialog being removed:
      // an action that deliberately moved focus elsewhere keeps it.
      const previous = opener.current
      if (previous && document.body.contains(previous)) previous.focus()
    }
  }, [open, onClose])

  return ref
}
